/**
 * ExpedienteCard — Ficha de un expediente en la Bandeja (rediseño v2).
 *
 * Estructura (izquierda → derecha):
 *  1. Barra de acento vertical (ámbar / azul / verde) según próxima acción.
 *  2. Monograma circular de la aseguradora con su color de marca + inicial,
 *     con el SLA (días) "enganchado" como mini-etiqueta.
 *  3. Bloque principal con jerarquía: nº siniestro pequeño en gris arriba
 *     junto a la aseguradora, asegurado en grande, dirección · provincia · CP
 *     en gris con icono de pin.
 *  4. Stepper horizontal: Contacto → Visita → Informe → Cierre, con fecha
 *     debajo de cada hito completado.
 *  5. Próxima acción (chip a color) + perito asignado (avatar + nombre).
 *  6. Chevron derecho que aparece al hover.
 *
 * Toda la ficha es un <Link> clicable y se eleva al hacer hover.
 */
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Phone, Calendar, FileText, Lock, MapPin, CaretRight, Check,
  WarningCircle, Snowflake, ChatCircleText,
} from "@phosphor-icons/react";

/* ---------- Colores de marca + assets por aseguradora ----------
 * `logo`: SVG local en /insurer-logos/. Carga instantánea, offline, sin
 *   dependencia externa. Es el primer recurso intentado.
 * `domain`: fallback para cargar el favicon oficial vía Google Favicons
 *   cuando no hay SVG local.
 * Si los dos fallan, se muestra el monograma de inicial con el color
 * de marca. */
const INSURER_BRAND = {
  mapfre:               { bg: "#E60022", fg: "#FFFFFF", logo: "/insurer-logos/mapfre.svg",        domain: "mapfre.com" },
  ocaso:                { bg: "#1D4ED8", fg: "#FFFFFF", logo: "/insurer-logos/ocaso.svg",         domain: "ocaso.es" },
  allianz:              { bg: "#003781", fg: "#FFFFFF", logo: "/insurer-logos/allianz.svg",       domain: "allianz.es" },
  axa:                  { bg: "#00008F", fg: "#FFFFFF", logo: "/insurer-logos/axa.svg",           domain: "axa.es" },
  reale:                { bg: "#0F172A", fg: "#FFFFFF", logo: "/insurer-logos/reale.svg",         domain: "reale.es" },
  generali:             { bg: "#B91C1C", fg: "#FFFFFF", logo: "/insurer-logos/generali.svg",      domain: "generali.es" },
  zurich:               { bg: "#2167AE", fg: "#FFFFFF", logo: "/insurer-logos/zurich.svg",        domain: "zurich.es" },
  occident:             { bg: "#9F1239", fg: "#FFFFFF", logo: "/insurer-logos/occident.svg",      domain: "occident.com" },
  "catalana occidente": { bg: "#9F1239", fg: "#FFFFFF", logo: "/insurer-logos/occident.svg",      domain: "catalanaoccidente.com" },
  pelayo:               { bg: "#10B981", fg: "#FFFFFF", logo: "/insurer-logos/pelayo.svg",        domain: "pelayo.com" },
  helvetia:             { bg: "#DC2626", fg: "#FFFFFF", logo: "/insurer-logos/helvetia.svg",      domain: "helvetia.es" },
  "linea directa":      { bg: "#FB923C", fg: "#FFFFFF", logo: "/insurer-logos/lineadirecta.svg",  domain: "lineadirecta.com" },
  "línea directa":      { bg: "#FB923C", fg: "#FFFFFF", logo: "/insurer-logos/lineadirecta.svg",  domain: "lineadirecta.com" },
  santalucia:           { bg: "#2563EB", fg: "#FFFFFF", logo: "/insurer-logos/santalucia.svg",    domain: "santalucia.es" },
  "santa lucia":        { bg: "#2563EB", fg: "#FFFFFF", logo: "/insurer-logos/santalucia.svg",    domain: "santalucia.es" },
  "santalucía":         { bg: "#2563EB", fg: "#FFFFFF", logo: "/insurer-logos/santalucia.svg",    domain: "santalucia.es" },
  caser:                { bg: "#0EA5E9", fg: "#FFFFFF", logo: "/insurer-logos/caser.svg",         domain: "caser.es" },
  liberty:              { bg: "#1E3A8A", fg: "#FFFFFF", domain: "libertyseguros.es" },
  mutua:                { bg: "#DC2626", fg: "#FFFFFF", domain: "mutua.es" },
  "mutua madrileña":    { bg: "#DC2626", fg: "#FFFFFF", domain: "mutua.es" },
  sanitas:              { bg: "#0EA5E9", fg: "#FFFFFF", domain: "sanitas.es" },
  asisa:                { bg: "#1D4ED8", fg: "#FFFFFF", domain: "asisa.es" },
  fiatc:                { bg: "#2563EB", fg: "#FFFFFF", domain: "fiatc.es" },
  plus:                 { bg: "#16A34A", fg: "#FFFFFF", domain: "plusultra.es" },
  "plus ultra":         { bg: "#16A34A", fg: "#FFFFFF", domain: "plusultra.es" },
  dkv:                  { bg: "#DC2626", fg: "#FFFFFF", domain: "dkvseguros.com" },
};

function getInsurerStyle(name) {
  if (!name) return { bg: "#6B7280", fg: "#FFFFFF", logo: null, domain: null };
  const key = String(name).trim().toLowerCase();
  return INSURER_BRAND[key] || { bg: "#7C3AED", fg: "#FFFFFF", logo: null, domain: null };
}

/* ---------- Próxima acción ----------
 * Determina la acción más urgente sobre el expediente para colorear la
 * barra de acento, el chip y para mostrar al perito qué hacer.
 */
function getNextAction(exp) {
  if (exp.estado_encargo === "congelado") {
    return { color: "info", label: "Standby", Icon: Snowflake };
  }
  if (exp.estado_encargo === "anulado") {
    return { color: "gray", label: "Anulado", Icon: WarningCircle };
  }
  if (exp.fecha_cierre) {
    return { color: "gray", label: "Cerrado", Icon: Lock };
  }
  // Contacto
  if (!exp.fecha_contacto) {
    return { color: "amber", label: "Contactar", Icon: Phone };
  }
  // Visita
  if (!exp.fecha_visita) {
    return { color: "amber", label: "Programar visita", Icon: Calendar };
  }
  // Visita programada a futuro
  if (exp.fecha_visita && !exp.visita_realizada) {
    const d = new Date(exp.fecha_visita);
    if (!isNaN(d)) {
      const ddmm = d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
      const hh = d.getHours();
      const mm = d.getMinutes();
      // Solo mostramos la hora si no es 00:00 (en cuyo caso lo más probable
      // es que sea sólo fecha sin hora real).
      const hhmm = (hh === 0 && mm === 0) ? "" : ` ${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
      return { color: "blue", label: `Visita · ${ddmm}${hhmm}`, Icon: Calendar };
    }
    return { color: "blue", label: "Visita agendada", Icon: Calendar };
  }
  // Preliminar
  if (exp.visita_realizada && !exp.fecha_preliminar) {
    return { color: "green", label: "Emitir preliminar", Icon: FileText };
  }
  // Final
  if (exp.fecha_preliminar && !exp.fecha_cierre) {
    return { color: "green", label: "Redactar informe", Icon: FileText };
  }
  return { color: "gray", label: "Sin acción", Icon: WarningCircle };
}

/* ---------- Pasos del workflow ---------- */
function getSteps(exp) {
  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
  };

  const contactoDone = !!exp.fecha_contacto;
  const visitaDone = !!exp.visita_realizada;
  const informeDone = !!exp.fecha_preliminar;
  const cierreDone = !!exp.fecha_cierre;

  // Step "actual": el primer pendiente.
  let current = "contacto";
  if (contactoDone && !visitaDone) current = "visita";
  else if (visitaDone && !informeDone) current = "informe";
  else if (informeDone && !cierreDone) current = "cierre";
  else if (cierreDone) current = null;

  const visitaSubtitle = visitaDone
    ? fmt(exp.fecha_visita_realizada || exp.fecha_visita)
    : exp.fecha_visita
      ? fmt(exp.fecha_visita)
      : null;

  return [
    { k: "contacto", label: "Contacto", done: contactoDone, current: current === "contacto", date: fmt(exp.fecha_contacto), pendingHint: !contactoDone && current === "contacto" ? "pendiente" : null },
    { k: "visita", label: "Visita", done: visitaDone, current: current === "visita", date: visitaSubtitle, pendingHint: !visitaDone && current === "visita" ? "pendiente" : null },
    { k: "informe", label: "Informe", done: informeDone, current: current === "informe", date: fmt(exp.fecha_preliminar), pendingHint: !informeDone && current === "informe" ? "en curso" : null },
    { k: "cierre", label: "Cierre", done: cierreDone, current: current === "cierre", date: fmt(exp.fecha_cierre), pendingHint: !cierreDone && current === "cierre" ? "pendiente" : null },
  ];
}

/* ---------- Tokens de color reutilizables ---------- */
const ACTION_TONE = {
  amber: {
    bar: "bg-status-warning-fg",
    chipBg: "bg-status-warning-bg",
    chipFg: "text-status-warning-fg",
    chipBorder: "border-status-warning-bd",
    // Stepper · paso actual: anillo y texto en este tono
    stepRing: "border-status-warning-fg ring-2 ring-status-warning-bd/40 text-status-warning-fg",
  },
  blue: {
    bar: "bg-status-info-fg",
    chipBg: "bg-status-info-bg",
    chipFg: "text-status-info-fg",
    chipBorder: "border-status-info-bd",
    stepRing: "border-status-info-fg ring-2 ring-status-info-bd/40 text-status-info-fg",
  },
  green: {
    bar: "bg-status-success-fg",
    chipBg: "bg-status-success-bg",
    chipFg: "text-status-success-fg",
    chipBorder: "border-status-success-bd",
    stepRing: "border-status-success-fg ring-2 ring-status-success-bd/40 text-status-success-fg",
  },
  info: {
    bar: "bg-status-info-fg",
    chipBg: "bg-status-info-bg",
    chipFg: "text-status-info-fg",
    chipBorder: "border-status-info-bd",
    stepRing: "border-status-info-fg ring-2 ring-status-info-bd/40 text-status-info-fg",
  },
  gray: {
    bar: "bg-border",
    chipBg: "bg-background",
    chipFg: "text-muted",
    chipBorder: "border-border",
    stepRing: "border-border ring-2 ring-border/40 text-muted",
  },
};

/* ---------- Subcomponentes ---------- */
function InsurerMonogram({ name, slaDays, customLogo }) {
  const { bg, fg, logo, domain } = getInsurerStyle(name);
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";

  // Cascada de fuentes de logo, recalculada cuando cambian `customLogo`,
  // `logo` o `domain` (la lista de aseguradoras llega asíncronamente y
  // `customLogo` puede aparecer después del primer render):
  //   1. Logo personalizado del admin (`aseguradora.logo_url`, data URL).
  //   2. SVG local (`/insurer-logos/<id>.svg`) — instantáneo, offline.
  //   3. Favicon oficial vía Google Favicons (si hay dominio mapeado).
  //   4. Monograma de inicial sobre color de marca.
  const [src, setSrc] = useState(null);
  const [imgOk, setImgOk] = useState(false);
  const [step, setStep] = useState(null);

  useEffect(() => {
    let nextStep, nextSrc;
    if (customLogo) { nextStep = "custom"; nextSrc = customLogo; }
    else if (logo) { nextStep = "local"; nextSrc = logo; }
    else if (domain) { nextStep = "favicon"; nextSrc = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`; }
    else { nextStep = null; nextSrc = null; }
    setStep(nextStep);
    setSrc(nextSrc);
    setImgOk(false);
  }, [customLogo, logo, domain]);

  const showLogo = !!src && imgOk;

  const handleError = () => {
    if (step === "custom" && logo) {
      setStep("local"); setImgOk(false); setSrc(logo); return;
    }
    if ((step === "custom" || step === "local") && domain) {
      setStep("favicon"); setImgOk(false);
      setSrc(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
      return;
    }
    setSrc(null);
  };

  const handleLoad = (e) => {
    // Google Favicons devuelve un globo gris ≤16px cuando no encuentra la
    // marca. SVGs locales y logos personalizados tienen tamaño normal.
    if (step === "favicon" && e.target.naturalWidth < 24) {
      handleError();
      return;
    }
    setImgOk(true);
  };

  // SLA: ámbar si ≥10 días, gris si congelado/null, verde si dentro de plazo.
  const slaColor =
    slaDays === null || slaDays === undefined
      ? "bg-background text-muted border-border"
      : slaDays >= 10
        ? "bg-status-warning-bg text-status-warning-fg border-status-warning-bd"
        : "bg-status-success-bg text-status-success-fg border-status-success-bd";

  return (
    <div className="relative flex-shrink-0">
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-[15px] shadow-sm overflow-hidden"
        style={{
          background: showLogo ? "#FFFFFF" : bg,
          color: fg,
          border: showLogo ? "1px solid rgba(0,0,0,.06)" : "none",
        }}
        title={name || ""}
      >
        {src && (
          <img
            key={src}
            src={src}
            alt={name || ""}
            referrerPolicy="no-referrer"
            onLoad={handleLoad}
            onError={handleError}
            className="w-full h-full object-cover"
            style={{ display: showLogo ? "block" : "none" }}
          />
        )}
        {!showLogo && <span>{initial}</span>}
      </div>
      {slaDays !== null && slaDays !== undefined && (
        <span
          className={`absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9.5px] font-bold px-1.5 py-px rounded-md border ${slaColor} whitespace-nowrap`}
          title={`${slaDays} día${slaDays === 1 ? "" : "s"} desde la apertura`}
        >
          {slaDays}d
        </span>
      )}
    </div>
  );
}

function StepperHorizontal({ steps, tone }) {
  const ringCls = tone?.stepRing || "border-status-info-fg ring-2 ring-status-info-bd/40 text-status-info-fg";
  return (
    <div className="flex items-start gap-0">
      {steps.map((s, i) => {
        const dotCls = s.done
          ? "bg-status-success-fg text-white border-status-success-fg"
          : s.current
            ? `bg-white ${ringCls}`
            : "bg-white text-muted border-border";
        return (
          <div key={s.k} className="flex-1 flex flex-col items-center min-w-0">
            <div className="w-full flex items-center">
              {i > 0 && (
                <div className={`h-px flex-1 ${steps[i - 1].done ? "bg-status-success-fg" : "bg-border"}`} />
              )}
              {i === 0 && <div className="flex-1" />}
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dotCls}`}
                aria-label={s.label}
              >
                {s.done && <Check className="w-3 h-3" weight="bold" />}
              </div>
              {i === steps.length - 1 && <div className="flex-1" />}
              {i < steps.length - 1 && (
                <div className={`h-px flex-1 ${s.done ? "bg-status-success-fg" : "bg-border"}`} />
              )}
            </div>
            <div className={`text-[10.5px] mt-1 font-medium ${s.done || s.current ? "text-foreground" : "text-muted"}`}>
              {s.label}
            </div>
            <div className="text-[10px] text-muted tabular-nums leading-tight min-h-[12px]">
              {s.date || s.pendingHint || ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PeritoTag({ codigo }) {
  if (!codigo) {
    return <span className="text-[11px] text-muted italic">Sin asignar</span>;
  }
  // Generar iniciales del código (p. ej. "AC" si es "A. Chaparro" o "Antonio Chaparro")
  const initials = String(codigo)
    .replace(/\./g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("");
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-foreground">
      <span className="w-5 h-5 rounded-full bg-background border border-border text-[9.5px] font-bold text-muted inline-flex items-center justify-center tabular-nums">
        {initials || "?"}
      </span>
      <span className="truncate max-w-[120px]">{codigo}</span>
    </span>
  );
}

/* ---------- Componente principal ---------- */
export default function ExpedienteCard({ exp, status, gabinete, aseguradora, aseguradoUnread = 0, testid }) {
  const ed = exp.extracted_data || {};
  const aseguradoraName = exp.detected_aseguradora || ed.aseguradora || "—";
  const action = getNextAction(exp);
  const steps = getSteps(exp);
  const tone = ACTION_TONE[action.color] || ACTION_TONE.gray;

  const slaDays = status?.color === "frozen" ? null : (status?.dias ?? null);

  const numGab = ed.numero_siniestro_gabinete;
  const numCia = ed.numero_siniestro;
  const asegurado = ed.asegurado_nombre || "";
  const direccion = ed.direccion_riesgo;
  const provincia = ed.provincia;
  const cp = ed.codigo_postal;

  // Fecha de alta (inclusión) en el ERP: la mostramos en primer vistazo.
  // Preferimos `created_at` (siempre presente); cae a `fecha_alta` /
  // `fecha_apertura` si vienen de fuentes históricas.
  const altaIso = exp.created_at || exp.fecha_alta || exp.fecha_apertura;
  let altaLabel = null;
  if (altaIso) {
    const d = new Date(altaIso);
    if (!isNaN(d)) {
      altaLabel = d.toLocaleDateString("es-ES", {
        day: "2-digit", month: "2-digit", year: "2-digit",
      });
    }
  }

  return (
    <Link
      to={`/review/${exp.id}`}
      data-testid={testid || `expediente-card-${exp.id}`}
      className="group block bg-white border border-border rounded-lg overflow-hidden hover:shadow-md hover:-translate-y-px transition-all duration-150"
    >
      <div className="flex items-stretch">
        {/* 1. Barra de acento vertical */}
        <div className={`w-1 flex-shrink-0 ${tone.bar}`} aria-hidden />

        {/* Contenido en grid de 3 columnas (escritorio).
            En móvil se apila y el stepper / acción pasan abajo. */}
        <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,1fr)_minmax(0,1fr)] gap-3 lg:gap-4 items-center px-3 py-3">
          {/* 2 + 3. Monograma + bloque principal */}
          <div className="flex items-center gap-3 min-w-0">
            <InsurerMonogram
              name={aseguradoraName}
              slaDays={slaDays}
              customLogo={aseguradora?.logo_url || null}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] text-muted truncate">
                <span className="font-mono tabular-nums">
                  {numGab || numCia || "—"}
                </span>
                <span className="text-foreground/80 font-medium">{aseguradoraName}</span>
                {gabinete?.short_label && (
                  <span
                    className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded"
                    style={{
                      background: gabinete.color ? `${gabinete.color}22` : "#f1f5f9",
                      color: gabinete.color || "#475569",
                    }}
                  >
                    {gabinete.short_label}
                  </span>
                )}
                {altaLabel && (
                  <span
                    className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-foreground/80 bg-background border border-border rounded px-1.5 py-[1px] tabular-nums whitespace-nowrap"
                    title={`Alta en sistema: ${altaIso?.slice(0, 10)}`}
                    data-testid={`expediente-card-alta-${exp.id}`}
                  >
                    <Calendar className="w-2.5 h-2.5" weight="bold" />
                    Alta {altaLabel}
                  </span>
                )}
              </div>
              {/* Línea principal: dirección (lo más útil para planificar
                  visitas). Si no hay dirección, mostramos el asegurado
                  como fallback. */}
              <div className="text-[14px] font-semibold text-foreground truncate leading-tight mt-0.5 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-muted flex-shrink-0" weight="regular" />
                <span className="truncate">
                  {[direccion, provincia, cp].filter(Boolean).join(" · ")
                    || asegurado
                    || "—"}
                </span>
                {aseguradoUnread > 0 && (
                  <span
                    title={`${aseguradoUnread} mensaje${aseguradoUnread === 1 ? "" : "s"} sin leer del asegurado`}
                    data-testid={`expediente-card-asegurado-unread-${exp.id}`}
                    className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0"
                  >
                    <ChatCircleText className="w-3 h-3" weight="fill" />
                    {aseguradoUnread}
                  </span>
                )}
              </div>
              {/* Asegurado (degradado a línea secundaria pequeña, gris) */}
              {asegurado && (
                <div
                  className="text-[11px] text-muted truncate mt-0.5"
                  title={`Asegurado: ${asegurado}`}
                >
                  {asegurado}
                </div>
              )}
            </div>
          </div>

          {/* 4. Stepper del workflow (oculto en móvil para no desbordar) */}
          <div className="hidden lg:block px-2">
            <StepperHorizontal steps={steps} tone={tone} />
          </div>

          {/* 5. Próxima acción + perito */}
          <div className="flex items-center justify-between lg:justify-end gap-3 min-w-0">
            <div className="flex flex-col items-start lg:items-end gap-1.5 min-w-0">
              <span
                className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${tone.chipBg} ${tone.chipFg} ${tone.chipBorder}`}
              >
                <action.Icon className="w-3.5 h-3.5" weight="regular" />
                <span className="truncate max-w-[180px]">{action.label}</span>
              </span>
              <PeritoTag codigo={ed.perito_codigo} />
            </div>
            <CaretRight
              className="w-4 h-4 text-muted/40 group-hover:text-foreground group-hover:translate-x-0.5 transition-all flex-shrink-0"
              weight="bold"
            />
          </div>
        </div>
      </div>
    </Link>
  );
}
